"use client";

import { useCallback, useMemo } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { readFacetText, resolveTraitValuePatch } from "./facet-text";

/**
 * THE browse URL schema — Phase 04.
 *
 * WHY IT EXISTS. Browse state was half-synced (audit F38): search, network,
 * sort, view mode and tab lived in `useState` while only `?page=` reached the
 * URL. A copied link therefore reproduced page 3 of the *unfiltered*,
 * default-sorted list — different items than the sender saw — refreshing reset
 * the filters but kept the page number, and Back could not undo a filter
 * change. Every browse surface had its own copy of that bug.
 *
 * THIS IS A SEMI-PUBLIC CONTRACT. Phase 05 inherits these parameter names
 * rather than redefining them, and shared links depend on them, so a rename is
 * a breaking change. Parameter names deliberately match what the backend and
 * the rest of the app already use — `network_id` and `category` are the names
 * `/currencies?network_id=` and Home's discovery filters (D048) use.
 *
 *   q            free-text search            all browse surfaces
 *   network_id   integer network id          /market, /collection
 *   category     category slug               /collection
 *   sort         one of SORT_KEYS            all, per-surface subset
 *   view         grid | table                /collection
 *   tab          surface-specific segment    /collection, /saved, /drops
 *   type         entity type                 /search, /galleries
 *   page         1-based integer             every numbered list
 *   trait        metadata trait name         collection Items tab
 *   value        metadata trait value        collection Items tab (requires `trait`)
 *
 * WRITES USE THE HISTORY API, NOT `router.push` — the D048 ruling, for the same
 * reason: `router.push` refetches the route's RSC payload on every control
 * press to change client-side query keys. The App Router integrates
 * `history.pushState` with `useSearchParams`, so the read side is unchanged.
 *
 * TYPING IS `replace`, DISCRETE CHANGES ARE `push`. A debounced search box
 * writing a history entry per keystroke makes Back useless; a filter, sort,
 * view or tab change is a discrete decision and must be reversible. That is the
 * distinction §26.5's "back/forward reverses a filter change" is about.
 *
 * INVALID VALUES FAIL SAFE, IN TWO STAGES, exactly as D048 established: shape
 * is always validated here, so `?network_id=<script>` can never reach a query;
 * membership against an async option list (networks, categories) is checked by
 * the caller once the list is known, because rejecting a valid shared
 * `?category=art` on the first render would flip the view and flip it back.
 */

export const SORT_PRESETS = {
  newest: {
    orderBy: "id",
    order: "desc"
  },
  oldest: {
    orderBy: "id",
    order: "asc"
  },
  "name-asc": {
    orderBy: "name",
    order: "asc"
  },
  "name-desc": {
    orderBy: "name",
    order: "desc"
  },
  "created-desc": {
    orderBy: "created_at",
    order: "desc"
  },
  "created-asc": {
    orderBy: "created_at",
    order: "asc"
  }
} as const satisfies Record<string, {
  orderBy: "id" | "name" | "created_at";
  order: "asc" | "desc";
}>;
export type SortKey = keyof typeof SORT_PRESETS;
export const SORT_KEYS = Object.keys(SORT_PRESETS) as SortKey[];
export function isSortKey(value: string | null | undefined): value is SortKey {
  return !!value && Object.prototype.hasOwnProperty.call(SORT_PRESETS, value);
}

/** The `orderBy`/`order` pair a sort key sends. One control, two parameters. */
export function sortToParams(key: SortKey) {
  return SORT_PRESETS[key];
}

/** Free text: length-capped, control characters stripped. Never trusted further. */
function readQuery(raw: string | null): string {
  if (!raw) return "";
  // Control characters are dropped by code point rather than by a regex
  // class, so no escape sequence and no raw control byte lives in this file.
  const cleaned = Array.from(raw).filter(ch => {
    const code = ch.codePointAt(0) ?? 0;
    return code > 31 && code !== 127;
  }).join("");
  return cleaned.slice(0, 120);
}

/** A positive integer, or null. `?network_id=<script>` yields null. */
function readPositiveInt(raw: string | null): number | null {
  if (!raw || !/^\d{1,9}$/.test(raw)) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** A slug-shaped value, or null. Membership is the caller's second stage. */
function readSlug(raw: string | null): string | null {
  if (!raw) return null;
  return /^[a-zA-Z0-9؀-ۿ_-]{1,64}$/.test(raw) ? raw : null;
}
function readOneOf<T extends string>(raw: string | null, allowed: readonly T[]): T | null {
  if (!raw) return null;
  return (allowed as readonly string[]).includes(raw) ? raw as T : null;
}
export type BrowseParamPatch = {
  q?: string | null;
  network_id?: number | null;
  category?: string | null;
  sort?: SortKey | null;
  view?: string | null;
  tab?: string | null;
  type?: string | null;
  page?: number | null;
  /** Metadata trait/value filter. See `readFacetText`; membership is the caller's job. */
  trait?: string | null;
  value?: string | null;
};
export type BrowseWriteOptions = {
  /** `push` for a discrete change (default), `replace` while typing. */
  history?: "push" | "replace";
  /**
   * Drop `?page=`. Defaults to true for every key except `page` itself: a
   * result set that just changed shape has no page 3 to stay on, and keeping
   * the number is how the old code showed an empty grid after filtering.
   */
  resetPage?: boolean;
};
export type UseBrowseParamsOptions<TTab extends string, TView extends string, TType extends string> = {
  /** Sort keys this surface supports. Anything else in the URL falls back. */
  sortKeys?: readonly SortKey[];
  defaultSort?: SortKey;
  tabs?: readonly TTab[];
  defaultTab?: TTab;
  views?: readonly TView[];
  defaultView?: TView;
  types?: readonly TType[];
  defaultType?: TType;
};
export function useBrowseParams<TTab extends string = string, TView extends string = string, TType extends string = string>(options: UseBrowseParamsOptions<TTab, TView, TType> = {}) {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const {
    sortKeys = SORT_KEYS,
    defaultSort = "newest" as SortKey,
    tabs,
    defaultTab,
    views,
    defaultView,
    types,
    defaultType
  } = options;
  const search = searchParams.toString();
  const parsed = useMemo(() => {
    const sp = new URLSearchParams(search);
    const sortRaw = sp.get("sort");
    return {
      q: readQuery(sp.get("q")),
      networkId: readPositiveInt(sp.get("network_id")),
      category: readSlug(sp.get("category")),
      sort: isSortKey(sortRaw) && sortKeys.includes(sortRaw) ? sortRaw : defaultSort,
      view: (views ? readOneOf(sp.get("view"), views) : null) ?? defaultView ?? null,
      tab: (tabs ? readOneOf(sp.get("tab"), tabs) : null) ?? defaultTab ?? null,
      type: (types ? readOneOf(sp.get("type"), types) : null) ?? defaultType ?? null,
      page: readPositiveInt(sp.get("page")) ?? 1,
      trait: readFacetText(sp.get("trait")),
      value: readFacetText(sp.get("value"))
    };
  }, [search, sortKeys, defaultSort, tabs, defaultTab, views, defaultView, types, defaultType]);

  /** Serialise a patch onto the current query. Exported shape used by `buildHref`. */
  const buildSearch = useCallback((rawPatch: BrowseParamPatch, resetPage: boolean) => {
    const next = new URLSearchParams(search);
    // Enforced here rather than at each call site: a caller that changes
    // `trait` without also naming a `value` gets the previous value
    // cleared automatically, so a future facet/consumer cannot reintroduce
    // a stale trait/value pair by forgetting to pass `value: null`.
    const patch = resolveTraitValuePatch(rawPatch);
    const write = (key: string, value: string | number | null | undefined, isDefault: boolean) => {
      if (value === undefined) return;
      if (value === null || value === "" || isDefault) next.delete(key);else next.set(key, String(value));
    };
    write("q", patch.q, false);
    write("network_id", patch.network_id, false);
    write("category", patch.category, false);
    write("sort", patch.sort, patch.sort === defaultSort);
    write("view", patch.view, patch.view === defaultView);
    write("tab", patch.tab, patch.tab === defaultTab);
    write("type", patch.type, patch.type === defaultType);
    write("trait", patch.trait, false);
    write("value", patch.value, false);

    // Page one is never written, so the canonical list URL stays canonical —
    // the same rule `canonicalFirstPageHref` exists for in `Pagination`.
    if (patch.page !== undefined) {
      if (patch.page === null || patch.page <= 1) next.delete("page");else next.set("page", String(patch.page));
    } else if (resetPage) {
      next.delete("page");
    }
    return next.toString();
  }, [search, defaultSort, defaultView, defaultTab, defaultType]);
  const buildHref = useCallback((patch_0: BrowseParamPatch, resetPage_0 = true) => {
    const qs = buildSearch(patch_0, resetPage_0);
    return qs ? `${pathname}?${qs}` : pathname;
  }, [buildSearch, pathname]);
  const set = useCallback((patch_1: BrowseParamPatch, writeOptions: BrowseWriteOptions = {}) => {
    const {
      history: mode = "push",
      resetPage: resetPage_1 = patch_1.page === undefined
    } = writeOptions;
    const url = buildHref(patch_1, resetPage_1);
    if (typeof window === "undefined") return;
    // Same-URL writes are dropped: a debounced search that settles on the
    // value already in the URL would otherwise push a duplicate entry that
    // Back appears to ignore.
    const current = `${window.location.pathname}${window.location.search}`;
    if (url === current) return;
    if (mode === "replace") window.history.replaceState(null, "", url);else window.history.pushState(null, "", url);
  }, [buildHref]);
  const clearAll = useCallback((keep: BrowseParamPatch = {}) => {
    if (typeof window === "undefined") return;
    const qs_0 = new URLSearchParams();
    Object.entries(keep).forEach(([key_0, value_0]) => {
      if (value_0 !== null && value_0 !== undefined && value_0 !== "") qs_0.set(key_0, String(value_0));
    });
    const url_0 = qs_0.toString() ? `${pathname}?${qs_0.toString()}` : pathname;
    window.history.pushState(null, "", url_0);
  }, [pathname]);
  return {
    ...parsed,
    set,
    clearAll,
    buildHref,
    pathname
  };
}